/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { useState, useRef } from 'react';
import { Card, Form, Button, Modal, InputGroup } from 'react-bootstrap';
import { MailIcon, LockClosedIcon, UserIcon } from '@heroicons/react/solid';

export interface INewUserModalProps {
  showModal?: boolean;
  createStudentLabel?: string;
  emailLabel?: string;
  firstNameLabel?: string;
  lastNameLabel?: string;
  passwordLabel?: string;
  confirmPasswordLabel?: string;
  iAgreeText?: string;
  inviteText?: string;
  tncLink?: string;
  submitButtonText?: string;
  signupButtonStyle?: any;
  alreadyHaveAnAccountText?: string;
  loginLink?: string;
  loginText?: string;
  country?: string;
  creditunion?: string;
  onAcknowledgement?(
    email: string,
    password: string,
    confirmPassword: string,
    tnc: boolean
  ): void;
  onDeny?(): void;
}

export const NewUserModal = ({
  showModal,
  createStudentLabel,
  lastNameLabel,
  emailLabel,
  firstNameLabel,
  passwordLabel,
  confirmPasswordLabel,
  iAgreeText,
  inviteText,
  tncLink,
  submitButtonText,
  signupButtonStyle,
  alreadyHaveAnAccountText,
  loginText,
  loginLink,
  country,
  creditunion,
  onDeny,
  onAcknowledgement,
}: INewUserModalProps) => {
  const [show, setShow] = useState(showModal);
  const emailRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);

  const handleClose = () => {
    if (onDeny) {
      onDeny();
    }
    setShow(false);
  };

  const _onFormSubmit = (event: any) => {
    if (event) {
      event.preventDefault();
      if (onAcknowledgement) {
        onAcknowledgement(
          emailRef?.current?.value,
          firstNameRef?.current?.value,
          lastNameRef?.current?.value,
          event?.target?.invite?.checked
        );
      }
      // 👇️ clear all input values in the form
      event.target.reset();
    }
    setShow(false);
  };

  return (
    <Modal centered show={show} onHide={handleClose}>
      <Modal.Header className="border-0">
        <Button variant="close" aria-label="Close" onClick={handleClose} />
      </Modal.Header>
      <Modal.Body className="p-0">
        <Card border="light" className="px-0 px-md-4 py-0 border-0">
          <Card.Header className="text-center text-md-center border-0 mb-0 mt-md-0">
            <h4>{createStudentLabel}</h4>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={_onFormSubmit}>
              <Form.Group id="email" className="mb-4">
                <Form.Label>{emailLabel}</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="text-gray-500">
                    <MailIcon className="icon icon-xs" />
                  </InputGroup.Text>
                  <Form.Control
                    ref={emailRef}
                    autoFocus
                    required
                    type="email"
                    placeholder="example@company.com"
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group id="firstName" className="mb-4">
                <Form.Label>{firstNameLabel}</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="text-gray-500">
                    <UserIcon className="icon icon-xs" />
                  </InputGroup.Text>
                  <Form.Control
                    ref={firstNameRef}
                    autoFocus
                    type="text"
                    placeholder="John"
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group id="lastName" className="mb-4">
                <Form.Label>{lastNameLabel}</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="text-gray-500">
                    <UserIcon className="icon icon-xs" />
                  </InputGroup.Text>
                  <Form.Control
                    ref={lastNameRef}
                    autoFocus
                    type="text"
                    placeholder="Doe"
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group id="lastName" className="mb-4">
                <Form.Label>{country}</Form.Label>
                <Form.Select aria-label="Default select example">
                  <option>Select Country</option>
                  <option value="1">Albania</option>
                  <option value="2">Dominica</option>
                  <option value="3">Jamaica</option>
                </Form.Select>
              </Form.Group>
              <Form.Group id="lastName" className="mb-4">
                <Form.Label>{creditunion}</Form.Label>
                <Form.Select aria-label="Default select example">
                  <option>Select Credit Union</option>
                  <option value="1">Pay Online</option>
                  <option value="2">Credit Union</option>
                </Form.Select>
              </Form.Group>

              <Form.Check type="checkbox" className="mb-4">
                <Form.Check.Input id="invite" className="me-2" />
                <Form.Check.Label htmlFor="invite" className="fw-normal mb-0">
                  {inviteText}
                </Form.Check.Label>
              </Form.Check>

              <div className="d-grid">
                <Button
                  variant="gray-800"
                  type="submit"
                  style={signupButtonStyle}
                >
                  {submitButtonText}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  );
};

NewUserModal.defaultProps = {
  showModal: false,
  createStudentLabel: 'Add New User',
  emailLabel: 'User Email',
  firstNameLabel: 'Enter First Name',
  lastNameLabel: 'Enter Last Name',
  inviteText: 'Send invite email',
  submitButtonText: 'Invite User',
  country: 'Select Country',
  creditunion: 'Select Credit Union',
};
